import { create } from 'zustand';
import type {
  TestSession,
  Question,
  TestAnswer,
  TestResult,
} from '../types/test';
import { apiClient } from '../services/apiClient';
import { useToastStore } from '../hooks/useToast';

interface TestState {
  currentSession: TestSession | null;
  isTestActive: boolean;
  currentQuestion: Question | null;
  timeRemaining: number;
  answers: TestAnswer[];
  isLoading: boolean;
  error: string | null;

  // Actions
  startTest: (testType: 'sample' | 'free' | 'standard' | 'comprehensive') => Promise<void>;
  submitAnswer: (
    questionId: string,
    answer: string | number,
    timeSpent: number
  ) => Promise<void>;
  skipQuestion: (questionId: string, timeSpent: number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (questionIndex: number) => void;
  pauseTest: () => void;
  resumeTest: () => void;
  completeTest: () => Promise<TestResult | null>;
  abandonTest: () => Promise<void>;
  updateTimeRemaining: (time: number) => void;
  resetTest: () => void;
  loadSession: (sessionId: string) => Promise<void>;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useTestStore = create<TestState>((set, get) => ({
  currentSession: null,
  isTestActive: false,
  currentQuestion: null,
  timeRemaining: 0,
  answers: [],
  isLoading: false,
  error: null,

  startTest: async testType => {
    try {
      set({ isLoading: true, error: null });

      // Get questions from the new randomized structure
      let questions: Question[];
      try {
        // Dynamic import to avoid module-level execution issues
        const { getRandomTest } = await import('../data/IQTest');

        // Map legacy test types to new structure (all free now)
        let actualTestType: 'sample' | 'free';
        if (testType === 'standard' || testType === 'comprehensive') {
          actualTestType = 'free';
        } else {
          actualTestType = testType as 'sample' | 'free';
        }

        questions = getRandomTest(actualTestType);
      } catch (error) {
        throw new Error(`Failed to load test questions: ${error}`);
      }

      // Try to create test session on backend
      let backendSession;
      try {
        // Map new test types to legacy API types (all free now)
        const apiTestType = testType === 'sample' || testType === 'free' ? 'standard' : testType;
        const response = await apiClient.createTestSession(apiTestType as 'standard' | 'comprehensive');

        if (!response.success) {
          throw new Error(response.message || 'Failed to create test session');
        }

        backendSession = response.data?.session;
        if (!backendSession) {
          throw new Error('Invalid session data received from server');
        }
      } catch (apiError) {
        // If backend is not available, create a mock session for development
        if (import.meta.env.DEV) {
          console.info('Backend not available, using mock session for development');
        }

        // Calculate time limits based on test type
        let timeLimit: number;
        if (testType === 'sample') {
          timeLimit = 90; // 15 minutes for sample
        } else if (testType === 'free' || testType === 'standard') {
          timeLimit = 180; // 30 minutes for standard
        } else if (testType === 'comprehensive') {
          timeLimit = 360; // 60 minutes for comprehensive (now free)
        } else {
          timeLimit = 1800; // default
        }

        backendSession = {
          id: `mock_session_${Date.now()}`,
          userId: null,
          testType: testType.toUpperCase(),
          status: 'IN_PROGRESS',
          createdAt: new Date().toISOString(),
          currentQuestionIndex: 0,
          questions: questions.map(q => ({
            id: q.id,
            questionType: q.type,
            category: q.category,
            difficulty: q.difficulty,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            timeLimit: q.timeLimit,
            points: q.points,
            explanation: q.explanation,
            pattern: q.pattern,
          })),
          answers: [],
          timeRemaining: timeLimit,
          totalTimeLimit: timeLimit,
        };
      }

      // Transform backend session to frontend format
      const session: TestSession = {
        id: backendSession.id,
        userId: backendSession.userId,
        testType: testType,
        status: backendSession.status.toLowerCase().replace('_', '-') as any,
        startedAt: new Date(backendSession.createdAt),
        currentQuestionIndex: backendSession.currentQuestionIndex || 0,
        questions:
          backendSession.questions?.map((q: any) => ({
            id: q.id,
            type: q.questionType,
            category: q.category,
            difficulty: q.difficulty,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            timeLimit: q.timeLimit,
            points: q.points,
            explanation: q.explanation,
            pattern: q.pattern,
          })) || questions,
        answers:
          backendSession.answers?.map((a: any) => ({
            questionId: a.questionId,
            selectedAnswer: a.selectedAnswer,
            timeSpent: a.timeSpent,
            isCorrect: a.isCorrect,
            timestamp: new Date(a.timestamp),
          })) || [],
        timeRemaining: backendSession.timeRemaining,
        totalTimeLimit: backendSession.totalTimeLimit,
      };

      set({
        currentSession: session,
        isTestActive: true,
        currentQuestion:
          session.questions[session.currentQuestionIndex] || null,
        timeRemaining: session.timeRemaining,
        answers: session.answers,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to start test:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to start test';

      set({
        error: errorMessage,
        isLoading: false,
      });

      // Show toast notification for test start failures
      const { addToast } = useToastStore.getState();
      addToast({
        type: 'error',
        title: 'Test Start Failed',
        message: errorMessage,
        duration: 5000,
      });
    }
  },

  submitAnswer: async (questionId, answer, timeSpent) => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    // Store answer immediately without validation for instant UI response
    const testAnswer: TestAnswer = {
      questionId,
      selectedAnswer: answer,
      timeSpent,
      isCorrect: false, // Will be calculated at the end
      timestamp: new Date(),
    };

    // Check if an answer for this question already exists
    const existingAnswerIndex = state.answers.findIndex(
      a => a.questionId === questionId
    );
    let updatedAnswers: TestAnswer[];

    if (existingAnswerIndex !== -1) {
      // Update existing answer
      updatedAnswers = [...state.answers];
      updatedAnswers[existingAnswerIndex] = testAnswer;
    } else {
      // Add new answer
      updatedAnswers = [...state.answers, testAnswer];
    }

    const updatedSession = {
      ...session,
      answers: updatedAnswers,
    };

    set({
      currentSession: updatedSession,
      answers: updatedAnswers,
    });

    // Optional: Submit to backend in background without blocking UI
    try {
      await apiClient.submitTestAnswer(session.id, {
        questionId,
        selectedAnswer: answer,
        timeSpent,
      });
    } catch (apiError) {
      // Silently fail - we'll calculate everything at the end anyway
      if (import.meta.env.DEV) {
        console.info('Background answer submission failed, will calculate at end');
      }
    }
  },

  skipQuestion: (questionId: string, timeSpent: number) => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    // Create a skipped answer entry
    const skippedAnswer: TestAnswer = {
      questionId,
      selectedAnswer: '', // empty string indicates skipped
      timeSpent,
      isCorrect: false,
      timestamp: new Date(),
    };

    // Check if an answer for this question already exists
    const existingAnswerIndex = state.answers.findIndex(
      a => a.questionId === questionId
    );
    let updatedAnswers: TestAnswer[];

    if (existingAnswerIndex !== -1) {
      // Update existing answer to skipped
      updatedAnswers = [...state.answers];
      updatedAnswers[existingAnswerIndex] = skippedAnswer;
    } else {
      // Add new skipped answer
      updatedAnswers = [...state.answers, skippedAnswer];
    }

    const updatedSession = {
      ...session,
      answers: updatedAnswers,
    };

    set({
      currentSession: updatedSession,
      answers: updatedAnswers,
    });
  },

  nextQuestion: () => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    const nextIndex = session.currentQuestionIndex + 1;
    if (nextIndex < session.questions.length) {
      const updatedSession = {
        ...session,
        currentQuestionIndex: nextIndex,
      };

      set({
        currentSession: updatedSession,
        currentQuestion: session.questions[nextIndex],
      });
    }
  },

  previousQuestion: () => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    const prevIndex = session.currentQuestionIndex - 1;
    if (prevIndex >= 0) {
      const updatedSession = {
        ...session,
        currentQuestionIndex: prevIndex,
      };

      set({
        currentSession: updatedSession,
        currentQuestion: session.questions[prevIndex],
      });
    }
  },

  goToQuestion: (questionIndex: number) => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    if (questionIndex >= 0 && questionIndex < session.questions.length) {
      const updatedSession = {
        ...session,
        currentQuestionIndex: questionIndex,
      };

      set({
        currentSession: updatedSession,
        currentQuestion: session.questions[questionIndex],
      });
    }
  },

  pauseTest: () => {
    set({ isTestActive: false });
  },

  resumeTest: () => {
    set({ isTestActive: true });
  },

  completeTest: async () => {
    const state = get();
    const session = state.currentSession;
    if (!session) return null;

    try {
      set({ isLoading: true, error: null });

      let result: TestResult;

      // Calculate correct answers now that test is complete
      const answersWithCorrectness = state.answers.map(answer => {
        const question = session.questions.find(q => q.id === answer.questionId);
        const isCorrect = question ? answer.selectedAnswer === question.correctAnswer : false;
        return { ...answer, isCorrect };
      });

      // Try to complete test on backend
      try {
        const response = await apiClient.completeTest(session.id);

        if (!response.success) {
          throw new Error(response.message || 'Failed to complete test');
        }

        const backendResult = response.data?.result;
        if (!backendResult) {
          throw new Error('Invalid result data received from server');
        }

        // Transform backend result to frontend format
        result = {
          id: backendResult.id,
          sessionId: backendResult.sessionId,
          iqScore: backendResult.iqScore,
          percentile: backendResult.percentile,
          totalQuestions: backendResult.totalQuestions,
          correctAnswers: backendResult.correctAnswers,
          timeSpent: backendResult.timeSpent,
          categoryScores: backendResult.categoryScores || [],
          detailedAnalysis: backendResult.detailedAnalysis,
          recommendations: backendResult.recommendations,
          generatedAt: new Date(backendResult.generatedAt),
        };
      } catch (apiError) {
        // If backend is not available, calculate result locally
        if (import.meta.env.DEV) {
          console.info('Backend not available, calculating result locally');
        }

        const correctAnswers = answersWithCorrectness.filter(a => a.isCorrect).length;
        const totalQuestions = session.questions.length;
        const accuracy =
          totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
        const timeSpent = session.totalTimeLimit - state.timeRemaining;

        const baseIQ = 100;
        const iqScore = Math.round(baseIQ + (accuracy - 50) * 0.6);
        const percentile = Math.min(
          99,
          Math.max(1, Math.round(accuracy * 1.2))
        );

        result = {
          id: `mock_result_${Date.now()}`,
          sessionId: session.id,
          iqScore: Math.max(70, Math.min(160, iqScore)),
          percentile,
          totalQuestions,
          correctAnswers,
          timeSpent,
          categoryScores: [],
          generatedAt: new Date(),
        };
      }

      // Update session with final answers including correctness
      const finalSession = {
        ...session,
        status: 'completed' as const,
        completedAt: new Date(),
        answers: answersWithCorrectness,
      };

      set({
        currentSession: finalSession,
        answers: answersWithCorrectness,
        isTestActive: false,
        isLoading: false,
      });



      return result;
    } catch (error) {
      console.error('Failed to complete test:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to complete test';

      set({
        error: errorMessage,
        isLoading: false,
      });

      // Show toast notification for test completion failures
      const { addToast } = useToastStore.getState();
      addToast({
        type: 'error',
        title: 'Test Completion Failed',
        message: 'Unable to save your test results. Please try again.',
        duration: 5000,
      });

      return null;
    }
  },

  abandonTest: async () => {
    const state = get();
    const session = state.currentSession;
    if (!session) return;

    try {
      set({ isLoading: true, error: null });

      // Try to abandon test on backend
      try {
        const response = await apiClient.abandonTest(session.id);

        if (!response.success) {
          throw new Error(response.message || 'Failed to abandon test');
        }
      } catch (apiError) {
        // If backend is not available, just continue silently
        if (import.meta.env.DEV) {
          console.info('Backend not available, abandoning test locally');
        }
      }

      const abandonedSession = {
        ...session,
        status: 'abandoned' as const,
      };

      set({
        currentSession: abandonedSession,
        isTestActive: false,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to abandon test:', error);
      set({
        error:
          error instanceof Error ? error.message : 'Failed to abandon test',
        isLoading: false,
      });
      // Don't throw error to prevent component crashes
    }
  },

  updateTimeRemaining: time => {
    set({ timeRemaining: time });
  },

  resetTest: () => {
    set({
      currentSession: null,
      isTestActive: false,
      currentQuestion: null,
      timeRemaining: 0,
      answers: [],
      isLoading: false,
      error: null,
    });
  },

  loadSession: async (sessionId: string) => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiClient.getTestSession(sessionId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to load test session');
      }

      const backendSession = response.data?.session;
      if (!backendSession) {
        throw new Error('Invalid session data received from server');
      }

      // Transform backend session to frontend format
      const session: TestSession = {
        id: backendSession.id,
        userId: backendSession.userId,
        testType: backendSession.testType.toLowerCase() as any,
        status: backendSession.status.toLowerCase().replace('_', '-') as any,
        startedAt: new Date(backendSession.createdAt),
        completedAt: backendSession.completedAt
          ? new Date(backendSession.completedAt)
          : undefined,
        currentQuestionIndex: backendSession.currentQuestionIndex || 0,
        questions:
          backendSession.questions?.map((q: any) => ({
            id: q.id,
            type: q.questionType,
            category: q.category,
            difficulty: q.difficulty,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            timeLimit: q.timeLimit,
            points: q.points,
          })) || [],
        answers:
          backendSession.answers?.map((a: any) => ({
            questionId: a.questionId,
            selectedAnswer: a.selectedAnswer,
            timeSpent: a.timeSpent,
            isCorrect: a.isCorrect,
            timestamp: new Date(a.timestamp),
          })) || [],
        timeRemaining: backendSession.timeRemaining || 0,
        totalTimeLimit:
          backendSession.totalTimeLimit ||
          (backendSession.testType === 'STANDARD' ? 1800 : 3600),
      };

      set({
        currentSession: session,
        isTestActive: session.status === 'in-progress',
        currentQuestion:
          session.questions[session.currentQuestionIndex] || null,
        timeRemaining: session.timeRemaining,
        answers: session.answers,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to load session:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to load test session',
        isLoading: false,
      });
      // Don't throw error to prevent component crashes
    }
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));
